import { Modal } from '@evisort/eds/components/Modal/Modal';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { BRAND_NAME } from '~/common/constants/brand';
import { WelcomeShell } from '~/eds/src/components/WelcomeShell/WelcomeShell';
import { useHomeOwnsWelcome } from '~/features/home/first-run/useHomeOwnsWelcome';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import {
  deletePageSearchParam,
  getPageSearchQueryByKey,
} from '~/utils/searchQuery';

import {
  useWelcomeTitle,
  WelcomeWorkspaceLines,
} from './WelcomeWorkspaceLines';

const QueryString = 'welcomeMessage';

const onboardingIds = [
  OnboardingIdType.SplashPageCardOne,
  OnboardingIdType.SplashPageCardTwo,
  OnboardingIdType.SplashPageCardThree,
];

export function Welcome() {
  const intl = useIntl();
  const enableCanduOnboarding = useFlag(FlagType.Onboarding);

  const { navigate } = useRouting();

  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    const hasWelcomeMessage = getPageSearchQueryByKey<string>(QueryString, '');

    if (hasWelcomeMessage === 'true') {
      setShowModal(true);
    }
  }, []);

  const { homeOwnsWelcome, isPending } = useHomeOwnsWelcome();
  useEffect(() => {
    if (homeOwnsWelcome) {
      setShowModal(false);
    }
  }, [homeOwnsWelcome]);

  const title = useWelcomeTitle();

  const children = enableCanduOnboarding ? (
    <WelcomeShell
      actions={[
        {
          level: 'primary',
          text: intl.formatMessage(
            {
              id: 'Welcome.setupEvisort',
              defaultMessage: 'Set up {brandName}',
              description: 'Button text for setting up the product',
            },
            { brandName: BRAND_NAME },
          ),
          onClick: () => {
            setShowModal(false);
            deletePageSearchParam(QueryString);
            navigate(RoutePathType.Onboarding);
          },
        },
      ]}
      description={intl.formatMessage(
        {
          id: 'Welcome.setupDescription',
          defaultMessage:
            "Finish setting up {brandName} using our guided configuration wizard and you'll be up and running.",
          description: 'Description for the product setup process',
        },
        { brandName: BRAND_NAME },
      )}
      title={title}
      subtitle={intl.formatMessage({
        id: 'Welcome.welcomeSubtitle',
        defaultMessage:
          'Get ready to experience intelligent contract management.',
        description: 'Welcome subtitle about contract management',
      })}
      onboardingIds={onboardingIds}
      enableLogo
    />
  ) : (
    <WelcomeWorkspaceLines />
  );

  return (
    <>
      {
        <Modal
          size="full-page"
          isVisible={
            showModal && enableCanduOnboarding && !homeOwnsWelcome && !isPending
          }
          onHide={() => {
            setShowModal(false);
            deletePageSearchParam(QueryString);
          }}
          header={{ title: '' }}
          footer={{
            actions: [
              {
                label: intl.formatMessage({
                  id: 'Welcome.skipSteps',
                  defaultMessage: 'Skip Steps',
                  description: 'Button text to skip onboarding steps',
                }),
                text: intl.formatMessage({
                  id: 'Welcome.skipSteps',
                  defaultMessage: 'Skip Steps',
                  description: 'Button text to skip onboarding steps',
                }),
                variant: 'tertiary',
                onClick: () => {
                  setShowModal(false);
                  deletePageSearchParam(QueryString);
                },
              },
            ],
          }}
        >
          {children}
        </Modal>
      }
      {
        <>
          <Modal
            isVisible={
              showModal &&
              !enableCanduOnboarding &&
              !homeOwnsWelcome &&
              !isPending
            }
            onHide={() => {
              setShowModal(false);
              deletePageSearchParam(QueryString);
            }}
            header={{ title }}
            footer={{
              actions: [
                {
                  label: intl.formatMessage({
                    id: 'Welcome.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Cancel button text',
                  }),
                  text: intl.formatMessage({
                    id: 'Welcome.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Cancel button text',
                  }),
                  variant: 'tertiary',
                  onClick: () => {
                    setShowModal(false);
                    deletePageSearchParam(QueryString);
                  },
                },
                {
                  label: intl.formatMessage(
                    {
                      id: 'Welcome.startWorking',
                      defaultMessage: 'Start working with {brandName}',
                      description: 'Button text to start using the product',
                    },
                    { brandName: BRAND_NAME },
                  ),
                  text: intl.formatMessage(
                    {
                      id: 'Welcome.startWorking',
                      defaultMessage: 'Start working with {brandName}',
                      description: 'Button text to start using the product',
                    },
                    { brandName: BRAND_NAME },
                  ),
                  variant: 'primary',
                  onClick: () => {
                    setShowModal(false);
                    deletePageSearchParam(QueryString);
                  },
                },
              ],
            }}
          >
            {children}
          </Modal>
        </>
      }
    </>
  );
}
